import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";

import { SearchInput } from "@similarweb/ui-components";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { DomainsFilter } from "app/pages/website-analysis/traffic-sources/display-ads/common/filters/DomainFilter";
import {
    DownloadExcelContainer,
    Right,
} from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/StyledComponents";
import { useDomainsFavicons } from "app/services/sitesService/hooks/useDomainsFavicons";
import type { IDomainFavicon } from "app/services/sitesService/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { DropDownContainer, SearchContainer } from "./styledComponents";

const EMPTY_FAVICONS_LIST: IDomainFavicon[] = [];

interface IFoldersFilters {
    orderBy: string;
    webSource: string;
    country: string;
    includeSubDomains: boolean;
    from: string;
    to: string;
    pageSize: number;
    page: number;
    timeGranularity: string;
    isWindow: boolean;
    keys: string;
    filter?: string;
}

interface IFoldersFilters {
    orderBy: string;
    webSource: string;
    country: string;
    includeSubDomains: boolean;
    from: string;
    to: string;
    pageSize: number;
    page: number;
    timeGranularity: string;
    isWindow: boolean;
    keys: string;
    filter?: string;
}

export const FoldersTableTop: FunctionComponent<any> = (props) => {
    const {
        isLoadingData,
        downloadExcelPermitted,
        excelLink,
        initialFilters,
        onFilterChange,
        keysArr,
        isCompare,
        currentDomain,
        setCurrentDomain,
    } = props;

    const services = useMemo(
        () => ({
            swNavigator: proNavigator,
            i18n: i18nFilter(),
        }),
        [],
    );

    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";

    const [search, setSearch] = useState<string>();
    const [filters, setFilters] = useState<IFoldersFilters>(initialFilters);

    const { data: faviconsList = EMPTY_FAVICONS_LIST } = useDomainsFavicons(keysArr as string[], {
        enabled: (keysArr as string[])?.length > 0,
    });

    const domainsList = useMemo(() => {
        return faviconsList.map((website) => ({
            name: website.Domain,
            displayName: website.Domain,
            icon: website.Favicon,
        }));
    }, [faviconsList]);

    const domainObj = useMemo(
        () => domainsList.find((domain) => domain.name === currentDomain),
        [domainsList, currentDomain],
    );

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent(
            "website.analysis.folders.table.excel.click",
            "submit-ok",
            "website.analysis.folders.table.excel.click",
        );
    };

    const onSearchChange = useCallback(
        (search) => {
            setSearch(search);
            const filterValue = `Folder;contains;"${search?.trim()}"`;
            onFilterChange({ filter: filterValue });
            setFilters((prevFilters) => ({
                ...prevFilters,
                filter: filterValue,
            }));
        },
        [onFilterChange],
    );

    const onDomainChange = useCallback(
        (newKey) => {
            const domainName = newKey.text;
            if (domainName === currentDomain) {
                return;
            }
            setCurrentDomain(domainName);
            onFilterChange({ keys: domainName });
            setFilters((prevFilters) => ({
                ...prevFilters,
                keys: domainName,
            }));
        },
        [currentDomain, onFilterChange],
    );

    return (
        !isLoadingData && (
            <>
                {isCompare && domainsList.length > 0 ? (
                    <DropDownContainer>
                        <DomainsFilter
                            onDomainFilterChange={onDomainChange}
                            selectedDomain={currentDomain}
                            domains={domainsList}
                        />
                    </DropDownContainer>
                ) : null}
                <SearchContainer withBorderTop={isCompare}>
                    <SearchInput
                        disableClear={true}
                        defaultValue={search}
                        debounce={500}
                        onChange={onSearchChange}
                        placeholder={services.i18n("forms.search.placeholder")}
                    />
                    <Right>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                            />
                        </DownloadExcelContainer>

                        <AddToDashboard
                            webSource="Total"
                            type="Table"
                            metric="LeadingFolders"
                            modelType="fromWebsite"
                            filters={filters}
                            overrideAddToDashboardParams={{
                                key: [
                                    {
                                        image: domainObj?.icon,
                                        name: domainObj?.name,
                                    },
                                ],
                            }}
                        />
                    </Right>
                </SearchContainer>
            </>
        )
    );
};
